// ============================================================================
// OpenClaw Redux Slice
//
// Manages: connection status, session key, gateway config, streaming state.
// Conversation messages are managed via the existing chatGPT reducer.
//
// The gateway URL and token are persisted to localStorage so the user
// doesn't have to re-enter them on every page load.
// ============================================================================
import { createSlice } from "@reduxjs/toolkit";
import { getAPIToken } from "@/utils/request";
import * as accountsReducer from "@/containers/Accounts/reducer";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as viewReducer from "@/containers/View/reducer";
// ---------------------------------------------------------------------------
// Defaults & localStorage persistence
// ---------------------------------------------------------------------------
const STORAGE_KEY_URL = "openclaw_gateway_url";
const STORAGE_KEY_TOKEN = "openclaw_gateway_token";
const DEFAULT_GATEWAY_URL = "ws://127.0.0.1:18789";
const DEFAULT_GATEWAY_TOKEN = "";
function loadFromStorage(key, fallback) {
    try {
        return localStorage.getItem(key) ?? fallback;
    }
    catch {
        return fallback;
    }
}
function saveToStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    }
    catch {
        // localStorage may be unavailable (e.g. incognito)
    }
}
export const AIBLE_TOOLS = [
    "aible_agent_search",
    "aible_analyze_dataset",
    "aible_dataset_nlq",
    "aible_deep_research",
    "aible_get_dataset_info",
    "aible_post_report_to_channel",
    "aible_prediction_tool",
    "aible_search_context",
    "aible_summarize_document",
    "aible_whats_changed"
];
const initialState = {
    connectionStatus: "disconnected",
    sessionKey: `aible-${crypto.randomUUID().slice(0, 8)}`,
    gatewayUrl: loadFromStorage(STORAGE_KEY_URL, DEFAULT_GATEWAY_URL),
    gatewayToken: loadFromStorage(STORAGE_KEY_TOKEN, DEFAULT_GATEWAY_TOKEN),
    streamingMessageKey: null,
    streamingQuestionIndex: undefined,
    currentToolCall: null,
    toolsDeny: [],
    agentFiles: {},
    agentFileDrafts: {},
    skillsStatus: null,
    sessionsUsage: null,
    error: null,
    sandboxes: [],
    plugins: {},
    activeRuns: {}
};
const openClawSlice = createSlice({
    name: "openClaw",
    initialState,
    reducers: {
        setConnectionStatus(state, action) {
            state.connectionStatus = action.payload;
            if (action.payload === "connected") {
                state.error = null;
            }
        },
        /** Set both URL and token at once */
        setGatewayConfig(state, action) {
            state.gatewayUrl = action.payload.url;
            state.gatewayToken = action.payload.token;
            saveToStorage(STORAGE_KEY_URL, action.payload.url);
            saveToStorage(STORAGE_KEY_TOKEN, action.payload.token);
        },
        /** Set just the gateway URL (for the input field) */
        setGatewayUrl(state, action) {
            state.gatewayUrl = action.payload;
            saveToStorage(STORAGE_KEY_URL, action.payload);
        },
        /** Set just the gateway token (for the input field) */
        setGatewayToken(state, action) {
            state.gatewayToken = action.payload;
            saveToStorage(STORAGE_KEY_TOKEN, action.payload);
        },
        setSessionKey(state, action) {
            state.sessionKey = action.payload;
        },
        /** Track which conversation key is the active streaming message */
        setStreamingMessageKey(state, action) {
            state.streamingMessageKey = action.payload;
            // DON'T clear currentToolCall here — tool events for the next step
            // may have already been stashed before the new streaming message exists
        },
        /** Track which conversation key is the active streaming message */
        setStreamingQuestionIndex(state, action) {
            state.streamingQuestionIndex = action.payload;
            // DON'T clear currentToolCall here — tool events for the next step
            // may have already been stashed before the new streaming message exists
        },
        /** Track the current tool call so it survives chat delta overwrites */
        setCurrentToolCall(state, action) {
            state.currentToolCall = action.payload;
        },
        setOpenClawError(state, action) {
            state.error = action.payload;
        },
        setToolsDeny(state, action) {
            state.toolsDeny = action.payload;
        },
        setSkillsStatus(state, action) {
            state.skillsStatus = action.payload;
        },
        setPlugins(state, action) {
            state.plugins = action.payload;
        },
        setActiveRuns(state, action) {
            state.activeRuns = action.payload;
        },
        setSessionsUsage(state, action) {
            state.sessionsUsage = action.payload;
        },
        setAgentFileContent(state, action) {
            const { name, content } = action.payload;
            state.agentFiles[name] = content;
            // Seed draft on first load if no in-progress edit exists
            if (!(name in state.agentFileDrafts) && content !== null) {
                state.agentFileDrafts[name] = content;
            }
        },
        setAgentFileDraft(state, action) {
            state.agentFileDrafts[action.payload.name] = action.payload.content;
        },
        clearAgentFileDraft(state, action) {
            delete state.agentFileDrafts[action.payload];
        },
        setOpenClawSandboxes(state, action) {
            state.sandboxes = action.payload;
        },
        resetOpenClawState() {
            return { ...initialState, sessionKey: `aible-${crypto.randomUUID().slice(0, 8)}` };
        }
    }
});
export const { setConnectionStatus, setGatewayConfig, setGatewayUrl, setGatewayToken, setSessionKey, setStreamingMessageKey, setStreamingQuestionIndex, setCurrentToolCall, setToolsDeny, setSkillsStatus, setPlugins, setSessionsUsage, setAgentFileContent, setAgentFileDraft, clearAgentFileDraft, setOpenClawError, setOpenClawSandboxes, resetOpenClawState, setActiveRuns } = openClawSlice.actions;
export default openClawSlice.reducer;
// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------
export const selectOpenClawConnectionStatus = (state) => state.openClaw?.connectionStatus ?? "disconnected";
export const selectOpenClawSessionKey = (state) => state.openClaw?.sessionKey ?? "";
/** Returns the conversation key (e.g. "3") of the message being streamed, or null */
export const selectOpenClawStreamingMessageKey = (state) => state.openClaw?.streamingMessageKey ?? null;
export const selectOpenClawStreaminQuestionIndex = (state) => state.openClaw?.streamingQuestionIndex ?? undefined;
export const selectOpenClawError = (state) => state.openClaw?.error ?? null;
export const selectCurrentToolCall = (state) => state.openClaw?.currentToolCall ?? null;
export const selectOpenClawGatewayUrl = (state) => state.openClaw?.gatewayUrl ?? DEFAULT_GATEWAY_URL;
export const selectOpenClawGatewayToken = (state) => state.openClaw?.gatewayToken ?? "";
export const selectOpenClawGatewayConfig = (state) => ({
    url: selectOpenClawGatewayUrl(state),
    token: selectOpenClawGatewayToken(state)
});
export const selectIsOpenClawConnected = (state) => state.openClaw?.connectionStatus === "connected";
export const getSandboxes = (state) => state.openClaw?.sandboxes ?? [];
export const selectToolsDeny = (state) => state.openClaw?.toolsDeny ?? [];
/** Tools from AIBLE_TOOLS not currently in the deny list */
export const selectAllowedTools = (state) => {
    const deny = selectToolsDeny(state);
    return AIBLE_TOOLS.filter((t) => !deny.includes(t));
};
/** Tools from AIBLE_TOOLS that are currently denied */
export const selectDeniedTools = (state) => {
    const deny = selectToolsDeny(state);
    return AIBLE_TOOLS.filter((t) => deny.includes(t));
};
/** Raw skills.status array (null = not yet fetched) */
export const selectSkillsStatus = (state) => state.openClaw?.skillsStatus ?? null;
export const selectPlugins = (state) => state.openClaw?.plugins;
export const selectActiveRuns = (state) => state.openClaw?.activeRuns;
/** Skills where eligible === true, shaped for SkillsList */
export const selectEligibleSkills = (state) => (state.openClaw?.skillsStatus ?? [])
    .filter((s) => s.eligible)
    .map((s) => ({ name: s.name, description: s.description ?? "" }));
/** Skills with unmet requirements, shaped for RequiredApiKeys */
export const selectMissingRequirements = (state) => (state.openClaw?.skillsStatus ?? [])
    .filter((s) => s.missing &&
    ((s.missing.bins?.length ?? 0) > 0 || (s.missing.env?.length ?? 0) > 0 || (s.missing.config?.length ?? 0) > 0))
    .map((s) => ({
    name: s.name,
    missing: {
        bins: s.missing?.bins ?? [],
        anyBins: s.missing?.anyBins ?? [],
        env: s.missing?.env ?? [],
        config: s.missing?.config ?? [],
        os: s.missing?.os ?? []
    }
}));
/** Aggregated sessions usage stats (null = not yet fetched) */
export const selectSessionsUsage = (state) => state.openClaw?.sessionsUsage ?? null;
/** Saved content for a specific agent file (null = not yet loaded) */
export const selectAgentFileContent = (name) => (state) => state.openClaw?.agentFiles?.[name] ?? null;
/** Current draft (in-progress edits) for a specific agent file */
export const selectAgentFileDraft = (name) => (state) => state.openClaw?.agentFileDrafts?.[name] ?? null;
/** Whether the draft differs from the saved content (i.e. has unsaved changes) */
export const selectAgentFileHasUnsavedChanges = (name) => (state) => {
    const saved = state.openClaw?.agentFiles?.[name] ?? null;
    const draft = state.openClaw?.agentFileDrafts?.[name] ?? null;
    return draft !== null && draft !== saved;
};
// ---------------------------------------------------------------------------
// Sandboxes
// ---------------------------------------------------------------------------
export function fetchAllSandboxes() {
    return async (dispatch, getState) => {
        dispatch(viewReducer.updateIsSandboxesLoading(true));
        const folderId = foldersReducer.getSelectedFolder(getState());
        const openshellBaseUrl = accountsReducer.getOpenShellBaseUrl(getState());
        const url = new URL(`${openshellBaseUrl}/sandboxes`);
        url.searchParams.append("folder_id", folderId);
        const apiToken = getAPIToken();
        if (apiToken === undefined) {
            return;
        }
        let retSandboxes = [];
        try {
            const sandboxRes = await fetch(url.toString(), {
                headers: { Authorization: "Bearer " + apiToken }
            });
            if (!sandboxRes.ok) {
                dispatch(viewReducer.updateIsSandboxesLoading(false));
                throw new Error(`${sandboxRes.status} ${sandboxRes.statusText}`);
            }
            retSandboxes = await sandboxRes.json();
            dispatch(setOpenClawSandboxes(retSandboxes));
        }
        catch (e) {
            throw new Error(e?.message ?? e);
        }
        finally {
            dispatch(viewReducer.updateIsSandboxesLoading(false));
        }
        return retSandboxes;
    };
}
